import { OrderStatus, Prisma } from "@prisma/client";
import { eventDateValues } from "./events-date.types";

const baseIncludedEventData = {
  dates: eventDateValues,
  address: true,
} satisfies Prisma.EventInclude;

export const eventDataValues = Prisma.validator<Prisma.EventDefaultArgs>()({
  include: baseIncludedEventData,
});

export type EventDataPrisma = Prisma.EventGetPayload<typeof eventDataValues>;

export const eventDefaultArgsStructureData = {
  include: {
    ...baseIncludedEventData,
    ticketTypes: {
      where: {
        pricing: {
          isPaid: true,
        },
        activityId: {
          not: null,
        },
      },
      include: {
        activity: true,
        buyableItem: {
          include: {
            orderItems: {
              where: {
                order: {
                  status: OrderStatus.success,
                },
              },
            },
          },
        },
      },
    },
    structureProducts: true,
  },
} satisfies Prisma.EventDefaultArgs;

const orderItemsWithOrderInclude = {
  where: {
    order: {
      status: OrderStatus.success,
    },
  },
  include: {
    order: true,
    creditNoteItem: {
      select: {
        creditNote: {
          select: {
            status: true,
            isFromVisitor: true,
          },
        },
      },
    },
  },
} satisfies Prisma.OrderItemFindManyArgs;

export const eventDefaultArgsOrganiserData = {
  include: {
    ...baseIncludedEventData,
    ticketTypes: {
      where: {
        pricing: {
          isPaid: true,
        },
        OR: [
          {
            activity: {
              structureId: null,
            },
          },
          {
            activityId: null,
          },
        ],
      },
      include: {
        buyableItem: {
          include: {
            orderItems: orderItemsWithOrderInclude,
          },
        },
      },
    },
    badgeTypes: {
      where: {
        pricing: {
          isPaid: true,
        },
      },
      include: {
        buyableItem: {
          include: {
            orderItems: orderItemsWithOrderInclude,
          },
        },
      },
    },
    cashRegisterCreditsBuyableItems: {
      include: {
        orderItems: orderItemsWithOrderInclude,
      },
    },
    advancedStatisticsBuyableItems: {
      include: {
        orderItems: orderItemsWithOrderInclude,
      },
    },
    promotionBuyableItems: {
      include: {
        orderItems: orderItemsWithOrderInclude,
      },
    },
    exportExhibitorsModuleBuyableItems: {
      include: {
        orderItems: orderItemsWithOrderInclude,
      },
    },
    exportProgramModuleBuyableItems: {
      include: {
        orderItems: orderItemsWithOrderInclude,
      },
    },
    eventCustomPdfAppearanceBuyableItems: {
      include: {
        orderItems: orderItemsWithOrderInclude,
      },
    },
    eventCustomAppearanceBuyableItems: {
      include: {
        orderItems: orderItemsWithOrderInclude,
      },
    },
    eventEquipmentRequests: {
      include: {
        buyableItem: {
          include: {
            orderItems: orderItemsWithOrderInclude,
          },
        },
        eventEquipmentAllocations: {
          include: {
            eventEquipment: true,
          },
        },
      },
    },
  },
} satisfies Prisma.EventDefaultArgs;

export const eventOrganiserDataValues = Prisma.validator<Prisma.EventDefaultArgs>()(
  eventDefaultArgsOrganiserData,
);

export type EventOrganiserDataPrisma = Prisma.EventGetPayload<typeof eventOrganiserDataValues>;

export const includedOrganiserData = {
  admin: true,
  events: eventDefaultArgsOrganiserData,
} satisfies Prisma.OrganiserInclude;

export const organiserDataTypeValues = Prisma.validator<Prisma.OrganiserDefaultArgs>()({
  include: includedOrganiserData,
});

export type OrganiserDataType = Prisma.OrganiserGetPayload<typeof organiserDataTypeValues>;

export const eventStructureDataValues = Prisma.validator<Prisma.EventDefaultArgs>()(
  eventDefaultArgsStructureData,
);

export type EventStructureDataPrisma = Prisma.EventGetPayload<typeof eventStructureDataValues>;

export const includedStructureData = {
  admin: true,
  address: true,
  businessSectorRelation: true,
  eventStructures: {
    include: {
      event: eventDefaultArgsStructureData,
    },
  },
} satisfies Prisma.StructureInclude;

export interface DataFilters {
  startDate?: Date;
  endDate?: Date;
}

export const organiserValidatedEventsDataSelect = {
  name: true,
  type: true,
  isPaidEntry: true,
  dates: eventDateValues,
  organiserEmail: true,
  organiserName: true,
  organiserPhone: true,
  fakeOrganiserName: true,
  organiser: {
    select: {
      admin: {
        select: {
          firstName: true,
          lastName: true,
        },
      },
    },
  },
  address: {
    select: {
      name: true,
      city: true,
      zipCode: true,
    },
  },
  eventAdditionalInformation: {
    select: {
      adoptedSolution: true,
      visitorCount: true,
      exhibitorCount: true,
      text: true,
      hasToBeRecontacted: true,
      interlocutorFirstName: true,
      interlocutorLastName: true,
    },
  },
} satisfies Prisma.EventSelect;
